import React, { useMemo, useCallback } from 'react';
import { PapiComment } from '@wix/da-papi-types';
import { flatThreadToTree } from '../../helpers';
import RecursiveComment from '../RecursiveComment';
import { CommentableTypes } from '../../../Comment/redux/types';
import { useScrollToLatestComment } from './useScrollToLatestComment';
import { useParentIndicators } from './useParentIndicators';
import { useReplyState } from './useReplyState';
import { useThreadChanged } from './useThreadChanged';
import { CommentPublisherResult } from '../../../CommentEditor/redux/types';
import { Props as CommentProps } from '../../../Comment/presets/Comment';

export interface Props {
  streamId: string;
  commentableTypeId: CommentableTypes;
  commentableItemId: number;
  parentId?: number;

  items: PapiComment[];
  maxDepth: number;
  maxDepthOverride?: number;
  maxCommentsShown?: number;
  canPostComment?: boolean;
  lastInsertedCommentId?: number;
  parentComment?: PapiComment;
  parentCommentRef?: React.RefObject<HTMLElement | null>;
  allowSelfInit?: boolean;
  replyIndentModifier?: number;
  showOnlyLastInsertedComment?: boolean;
  className?: string;
  commentClassName?: string;
  commentWrapperClassName?: string;
  viewRepliesClassName?: string;
  /** We have a special mode for forums where the top comment is chopped off by backend, this then require some "adjustments" to how we render things */
  enableForumThreadRootlessThreadMode?: boolean;
  commentComponent?: React.ComponentType<CommentProps>;
  onOpenCommentModal: (
    typeId: number,
    itemId: number,
    commentId: number,
    comment: PapiComment
  ) => void;
  onThreadUpdated: (streamId: string, result: CommentPublisherResult) => void;
}

const ThreadStack: React.FC<Props> = ({
  items,
  maxDepth,
  maxCommentsShown,
  lastInsertedCommentId,
  parentComment,
  parentCommentRef,
  commentableItemId,
  commentableTypeId,
  onOpenCommentModal,
  streamId,
  parentId,
  canPostComment,
  allowSelfInit,
  onThreadUpdated,
  commentComponent,
  commentClassName,
  commentWrapperClassName,
  viewRepliesClassName,
  enableForumThreadRootlessThreadMode,
  maxDepthOverride,
  replyIndentModifier,
  className,
  showOnlyLastInsertedComment,
}) => {
  const renderItems = useMemo(() => {
    if (showOnlyLastInsertedComment) {
      return flatThreadToTree(
        items.filter(comment => comment.commentId === lastInsertedCommentId),
        maxDepth
      );
    }

    const limitedItems = maxCommentsShown ? items.slice(0, maxCommentsShown) : items;
    return flatThreadToTree(limitedItems, maxDepth);
  }, [items, maxCommentsShown, maxDepth, showOnlyLastInsertedComment, lastInsertedCommentId]);

  const onThreadUpdatedCallback = useCallback(
    (result: CommentPublisherResult) => onThreadUpdated(streamId, result),
    [onThreadUpdated, streamId]
  );

  useThreadChanged(commentableItemId, commentableTypeId, parentId, allowSelfInit, maxDepthOverride);

  const { onEditingStateChange, isReplyingToComment } = useReplyState();

  const { setLastComment } = useScrollToLatestComment(lastInsertedCommentId);

  const {
    setVerticalIndicatorRef,
    setCommentRef: setCommentRefForIndicators,
    onCommentMouseOver,
  } = useParentIndicators(parentComment, parentCommentRef);

  const setCommentRef = useCallback(
    (commentId: number, element: HTMLDivElement | null) => {
      setCommentRefForIndicators(commentId, element);

      if (lastInsertedCommentId && lastInsertedCommentId === commentId) {
        setLastComment(element);
      }
    },
    [lastInsertedCommentId, setLastComment, setCommentRefForIndicators]
  );

  if (renderItems.length === 0) {
    return null;
  }

  return (
    <div className={className}>
      {renderItems.map(item => (
        <RecursiveComment
          key={`comment-wrap-${item.commentId}`}
          index={item.index}
          commentableItemId={commentableItemId}
          commentableTypeId={commentableTypeId}
          canPostComment={canPostComment}
          streamId={streamId}
          comment={item}
          maxDepth={maxDepth}
          commentComponent={commentComponent}
          commentClassName={commentClassName}
          commentWrapperClassName={commentWrapperClassName}
          viewRepliesClassName={viewRepliesClassName}
          enableForumThreadRootlessThreadMode={enableForumThreadRootlessThreadMode}
          replyIndentModifier={replyIndentModifier}
          isReplyingToParent={isReplyingToComment(item.parentId)}
          setCommentRef={setCommentRef}
          onCommentMouseOver={onCommentMouseOver}
          setVerticalIndicatorRef={setVerticalIndicatorRef}
          onEditingStateChange={onEditingStateChange}
          onOpenCommentModal={onOpenCommentModal}
          onThreadUpdated={onThreadUpdatedCallback}
        />
      ))}
    </div>
  );
};
ThreadStack.displayName = 'ThreadStack';

export default ThreadStack;
